/**
 * Dane rysunku rzutu dachu z Projektu 3D (etap A planu `zadania/2026-10_projekt-3d-kowalski.md`).
 *
 * Czyste funkcje bez three.js i bez pdf-lib. Geometrię biorą z `bryla.js`, `panele.js` i `analiza.js` tylko do
 * odczytu: układ modułów to ten sam `ukladProjektu`, który rysuje scena, więc rysunek i model się nie rozjadą.
 *
 * Dwa układy współrzędnych, oba w metrach:
 * - rzut [X, Y]: widok z góry obrócony tak, żeby dłuższy bok głównego skrzydła był poziomo (Y w dół kartki);
 *   `polnoc` mówi, o ile stopni zgodnie z ruchem wskazówek zegara od góry kartki leży prawdziwa północ;
 * - połać [u, v]: płaszczyzna połaci jak w `panele.js` (u wzdłuż okapu, v w górę spadku, prawdziwe długości),
 *   patrząc na połać z zewnątrz u rośnie w prawo.
 *
 * Przeszkody: `ukladProjektu` zwraca listę `przeszkody` ({ rodzaj, nazwa, polacId, obrys, strefa } w osiach połaci,
 * umowa w `zadania/2026-10_teczka-instalacji.md`) i bierzemy ją wprost; bez niej (starszy układ) liczymy komin
 * i okno dachowe sami. Połać schowana częściowo w innym skrzydle ma `czesci` (widoczne kawałki): rysujemy tylko
 * widoczne odcinki jej krawędzi, a kosze z `bryla.kosze` jak kalenice.
 */
import { zbudujBryle, uvPolaci } from './bryla'
import { obszarRoboczy } from './panele'
import { ukladProjektu, brylaKomina, polacPod, wysokoscDachu } from './analiza'
import { brylaLukarny } from './przeszkody'
import { modulProjektu, typElementu, TYPY_MODULOW } from './projekt'

const RAD = Math.PI / 180
const KIERUNKI = ['północna', 'północno-wschodnia', 'wschodnia', 'południowo-wschodnia', 'południowa', 'południowo-zachodnia', 'zachodnia', 'północno-zachodnia']
const LITERY = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
// Grubość ramy modułu na rysunku (podwójna kreska jak u właściciela).
export const RAMA_MODULU = 0.03

/** Kierunek połaci słowami z prawdziwego azymutu (8 stron). */
export const kierunekPolaci = (azymut) => KIERUNKI[Math.round((((azymut % 360) + 360) % 360) / 45) % 8]

const normujKat = (a) => ((a % 360) + 360) % 360

/** Nazwa modułu do legendy: model z katalogu albo typowy moduł rodzaju. */
export const nazwaModulu = (ustawienia, modul) => {
  const m = ustawienia?.model
  if (m) {
    const nazwa = m.nazwa || [m.producent, m.seria].filter(Boolean).join(' ')
    if (nazwa) return m.producent && !nazwa.toLowerCase().includes(m.producent.toLowerCase()) ? `${m.producent} ${nazwa}` : nazwa
  }
  const typ = TYPY_MODULOW.find((t) => t.id === modul.typ)
  return [typ?.nazwa, `${Math.round(modul.moc)} W`].filter(Boolean).join(' ')
}

// Przekrój wielokąta prostą v = const: [lo, hi] wzdłuż u albo null.
export const przekrojPoziomy = (w, v) => {
  let lo = Infinity
  let hi = -Infinity
  for (let i = 0; i < w.length; i += 1) {
    const a = w[i]
    const b = w[(i + 1) % w.length]
    if ((a[1] - v) * (b[1] - v) > 0) continue
    if (Math.abs(b[1] - a[1]) < 1e-12) { lo = Math.min(lo, a[0], b[0]); hi = Math.max(hi, a[0], b[0]); continue }
    const u = a[0] + ((b[0] - a[0]) * (v - a[1])) / (b[1] - a[1])
    lo = Math.min(lo, u)
    hi = Math.max(hi, u)
  }
  return lo <= hi ? [lo, hi] : null
}

const zakresPunktow = (punkty) => {
  const xs = punkty.map((p) => p[0])
  const ys = punkty.map((p) => p[1])
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) }
}

/**
 * Obrót rysunku: dłuższy bok głównego skrzydła poziomo, a z dwóch takich położeń to, w którym północ jest bliżej
 * góry kartki. Zwraca { obrot (stopnie), naRzut([x, z]) -> [X, Y], polnoc }.
 */
export const obrotRysunku = ({ skrzydla = [], zbieznosc = 0 }) => {
  const glowne = skrzydla.find((s) => s.id === 'dom') || skrzydla[0]
  const bazowy = glowne ? (glowne.obrot || 0) + ((glowne.dlugosc ?? 10) < (glowne.szerokosc ?? 8) ? 90 : 0) : 0
  // Prawdziwa północ w układzie sceny (x na wschód, z na południe): azymut siatki równy −zbieżność.
  const pn = [-Math.sin(zbieznosc * RAD), -Math.cos(zbieznosc * RAD)]
  const wariant = (kat) => {
    const c = Math.cos(kat * RAD)
    const s = Math.sin(kat * RAD)
    const naRzut = ([x, z]) => [x * c - z * s, x * s + z * c]
    const [nx, ny] = naRzut(pn)
    return { obrot: normujKat(kat), naRzut, polnoc: normujKat(Math.atan2(nx, -ny) / RAD), wGore: -ny }
  }
  const a = wariant(bazowy)
  const b = wariant(bazowy + 180)
  const { wGore, ...wynik } = b.wGore > a.wGore + 1e-9 ? b : a
  return wynik
}

/* ═══ Przeszkody ═════════════════════════════════════════════════════════════════════════════════ */

// Punkt połaci (u, v) w scenie.
const zUv = (polac, [a, b]) => polac.poczatek.map((p0, i) => p0 + polac.u[i] * a + polac.v[i] * b)

// Pionowy rzut punktu (x, z) na płaszczyznę połaci.
const naPlaszczyzne = (polac, x, z) => {
  const [nx, ny, nz] = polac.normalna
  const p0 = polac.wierzcholki[0]
  return [x, p0[1] - (nx * (x - p0[0]) + nz * (z - p0[2])) / ny, z]
}

const prostokatUv = (punkty, zapas) => {
  const us = punkty.map((p) => p[0])
  const vs = punkty.map((p) => p[1])
  const [u0, v0, u1, v1] = [Math.min(...us) - zapas, Math.min(...vs) - zapas, Math.max(...us) + zapas, Math.max(...vs) + zapas]
  return [[u0, v0], [u1, v0], [u1, v1], [u0, v1]]
}

/**
 * Komin i okno dachowe policzone jak w `analiza.js` (zanim przyjdzie `uklad.przeszkody`): `obrys` to kształt na
 * połaci, `strefa` to prostokąt, który wycina moduły (ten sam co w `ukladNaPolaci`).
 */
export const przeszkodyZElementow = (bryla, elementy = []) => elementy.flatMap((e) => {
  if (e.typ !== 'komin' && e.typ !== 'okno_dachowe') return []
  const [x, , z] = e.pozycja || []
  if (!Number.isFinite(x) || !Number.isFinite(z)) return []
  const polac = polacPod(bryla, x, z)
  if (!polac || Math.abs(polac.normalna[1]) < 1e-6) return []
  const uv = (p) => uvPolaci(polac, p)
  if (e.typ === 'komin') {
    const k = brylaKomina(bryla, e)
    const [w, h, d] = k.wymiary
    const t = (k.obrot || 0) * RAD
    const podstawa = [[-w / 2, -d / 2], [w / 2, -d / 2], [w / 2, d / 2], [-w / 2, d / 2]]
      .map(([lx, lz]) => [x + lx * Math.cos(t) + lz * Math.sin(t), z - lx * Math.sin(t) + lz * Math.cos(t)])
    const naroza = []
    for (const dx of [-w / 2, w / 2]) for (const dy of [-h / 2, h / 2]) for (const dz of [-d / 2, d / 2]) naroza.push([k.srodek[0] + dx, k.srodek[1] + dy, k.srodek[2] + dz])
    return [{
      id: e.id, rodzaj: 'komin', nazwa: 'Komin', polacId: polac.id,
      obrys: podstawa.map(([px, pz]) => uv(naPlaszczyzne(polac, px, pz))),
      strefa: prostokatUv(naroza.map(uv), 0.3),
    }]
  }
  const dd = { ...typElementu('okno_dachowe').domyslne, ...(e.parametry || {}) }
  const yd = wysokoscDachu(bryla, x, z) ?? 0
  const [uc, vc] = uv([x, yd, z])
  const r = Math.max(dd.szerokosc, dd.dlugosc) / 2
  return [{
    id: e.id, rodzaj: 'okno_dachowe', nazwa: 'Okno dachowe', polacId: polac.id,
    obrys: prostokatUv([[uc - dd.szerokosc / 2, vc - dd.dlugosc / 2], [uc + dd.szerokosc / 2, vc + dd.dlugosc / 2]], 0),
    strefa: prostokatUv([[-r, -r], [r, -r], [r, r], [-r, r]].map(([a, b]) => uv([x + a, yd, z + b])), 0.2),
  }]
})

/* ═══ Widoczne części połaci ═════════════════════════════════════════════════════════════════════ */

/** Część [t0, t1] odcinka a→b (punkty [x, z]) leżąca w wielokącie wypukłym `w`, z tolerancją 1 mm; null poza nim. */
export const odcinekWWypuklym = (a, b, w, tol = 1e-3) => {
  let pole = 0
  for (let i = 0; i < w.length; i += 1) pole += w[i][0] * w[(i + 1) % w.length][1] - w[(i + 1) % w.length][0] * w[i][1]
  const zn = pole >= 0 ? 1 : -1
  let t0 = 0
  let t1 = 1
  for (let i = 0; i < w.length; i += 1) {
    const p = w[i]
    const q = w[(i + 1) % w.length]
    const dl = Math.hypot(q[0] - p[0], q[1] - p[1])
    if (dl < 1e-9) continue
    // Normalna do środka wielokąta (lewa przy obiegu przeciwnym do wskazówek zegara), jednostkowa.
    const n = [(zn * -(q[1] - p[1])) / dl, (zn * (q[0] - p[0])) / dl]
    const fa = n[0] * (a[0] - p[0]) + n[1] * (a[1] - p[1]) + tol
    const fb = n[0] * (b[0] - p[0]) + n[1] * (b[1] - p[1]) + tol
    if (fa < 0 && fb < 0) return null
    if (fa >= 0 && fb >= 0) continue
    const t = fa / (fa - fb)
    if (fa < 0) t0 = Math.max(t0, t)
    else t1 = Math.min(t1, t)
  }
  return t1 - t0 > 1e-6 ? [t0, t1] : null
}

// Przedziały t odcinka a→b widoczne w którymś z kawałków (bez kawałków: cały odcinek).
const widoczneOdcinki = (a, b, czesci) => {
  if (!czesci) return [[0, 1]]
  const przedzialy = czesci.map((c) => odcinekWWypuklym(a, b, c)).filter(Boolean).sort((x, y) => x[0] - y[0])
  const wynik = []
  for (const [t0, t1] of przedzialy) {
    const ost = wynik[wynik.length - 1]
    if (ost && t0 <= ost[1] + 1e-4) ost[1] = Math.max(ost[1], t1)
    else wynik.push([t0, t1])
  }
  return wynik
}

// Przedziały bez części leżących w którymkolwiek z wielokątów `zakryte` (z tolerancją 1 cm, więc także na ich brzegu).
const bezZakrytych = (przedzialy, a, b, zakryte) => {
  let wynik = przedzialy
  for (const w of zakryte) {
    const z = odcinekWWypuklym(a, b, w, 0.01)
    if (!z) continue
    wynik = wynik.flatMap(([t0, t1]) => [[t0, Math.min(t1, z[0])], [Math.max(t0, z[1]), t1]].filter(([x, y]) => y - x > 1e-4))
  }
  return wynik
}

const naOdcinku = (a, b, t) => a.map((x, i) => x + (b[i] - x) * t)

const wWielokacie = ([x, z], w) => {
  let wyn = false
  for (let i = 0, j = w.length - 1; i < w.length; j = i, i += 1) {
    const [xi, zi] = w[i]
    const [xj, zj] = w[j]
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) wyn = !wyn
  }
  return wyn
}

// Kosz leży na połaci, gdy oba końce są w jej płaszczyźnie, a środek w jej rzucie.
const koszNaPolaci = (p, [a, b]) => {
  const odl = (q) => Math.abs(p.normalna.reduce((s, n, i) => s + n * (q[i] - p.wierzcholki[0][i]), 0))
  const sr = [(a[0] + b[0]) / 2, (a[2] + b[2]) / 2]
  return odl(a) < 0.03 && odl(b) < 0.03 && wWielokacie(sr, p.wierzcholki.map((w) => [w[0], w[2]]))
}

/* ═══ Dane rzutu ═════════════════════════════════════════════════════════════════════════════════ */

const RODZAJ_KRAWEDZI = { okap: 'okap', kalenica: 'kalenica', naroze: 'naroze', szczytowa: 'szczytowa', zalamanie: 'zalamanie' }

/**
 * Dane do rysunku dachu z projektu. `bryla`, `uklad` i `modul` można podać z ekranu (już policzone), inaczej
 * liczymy je z projektu. Bez adresu i podkładu mapy (decyzja właściciela 2026-10-23: tak widzą rysunek firmy).
 *
 * @returns {{
 *   rodzaj, modul: { nazwa, moc, dlugosc, szerokosc }, liczba, mocKwp, obrot, polnoc,
 *   polacie: [{ id, litera, plaska, nachylenie, azymut, kierunek, plan, uv, czesci, czesciUv, spadek, krawedzie,
 *              kosze, obszarRoboczy, rzedy, moduly: [{ nr, rzad, plan, uv }],
 *              przeszkody: [{ rodzaj, nazwa, plan, uv, strefaPlan, strefaUv, liniePlan, linieUv }] }],
 *   kosze, sciany, obszar, zakres
 * }}
 */
export const daneRzutu = (projekt, { bryla: brylaGotowa, uklad: ukladGotowy, modul: modulGotowy } = {}) => {
  const lok = projekt.lokalizacja || {}
  const zbieznosc = lok.zbieznosc ?? 0
  const modul = modulGotowy || modulProjektu(projekt.moduly)
  const bryla = brylaGotowa || zbudujBryle(projekt.budynek)
  const uklad = ukladGotowy || ukladProjektu({
    bryla, projekt: { ...projekt, elementy: (projekt.elementy || []).filter((e) => typElementu(e.typ)?.naDachu) },
    modul, lat: lok.lat ?? 52, lon: lok.lon ?? 19, zbieznosc,
  })
  const grunt = projekt.rodzaj === 'grunt'
  const { obrot, naRzut, polnoc } = obrotRysunku({ skrzydla: projekt.budynek?.skrzydla || [], zbieznosc })
  const plan = (p) => naRzut([p[0], p[2]])
  // Kierunek spadku na rysunku: poziomy rzut normalnej połaci (obrót rysunku nie przesuwa wektorów).
  const kierunekSpadku = (n) => {
    const [x, y] = naRzut([n[0], n[2]])
    const d = Math.hypot(x, y) || 1
    return [x / d, y / d]
  }
  // Widoczne odcinki krawędzi połaci: w jej kawałkach (`czesci`), bez wielokątów `zakryte` ([x, z] sceny).
  const krawedzieWidoczne = (p, rodzaje, zakryte) => p.wierzcholki.flatMap((w, i) => {
    const w1 = p.wierzcholki[(i + 1) % p.wierzcholki.length]
    const a2 = [w[0], w[2]]
    const b2 = [w1[0], w1[2]]
    const czesci = p.czesci ? p.czesci.map((c) => c.map((q) => [q[0], q[2]])) : null
    return bezZakrytych(widoczneOdcinki(a2, b2, czesci), a2, b2, zakryte).map(([t0, t1]) => {
      const a = naOdcinku(w, w1, t0)
      const b = naOdcinku(w, w1, t1)
      return { rodzaj: RODZAJ_KRAWEDZI[rodzaje[i]] || 'okap', plan: [plan(a), plan(b)], uv: [uvPolaci(p, a), uvPolaci(p, b)] }
    })
  })
  const margines = projekt.moduly?.margines ?? 0.3

  const przeszkody = Array.isArray(uklad.przeszkody) ? uklad.przeszkody : przeszkodyZElementow(bryla, projekt.elementy)
  const polacie = (grunt ? [] : bryla.polacie || []).map((p) => {
    const plaska = p.nachylenie < 5
    const azymut = Math.round(normujKat(p.azymut + zbieznosc))
    const uvW = p.wierzcholki.map((w) => uvPolaci(p, w))
    const rodzaje = p.rodzajeKrawedzi || []
    return {
      id: p.id,
      skrzydloId: p.skrzydloId,
      plaska,
      nachylenie: Math.round(p.nachylenie),
      azymut,
      kierunek: plaska ? null : kierunekPolaci(azymut),
      plan: p.wierzcholki.map(plan),
      uv: uvW,
      // Widoczne kawałki połaci (bez części schowanej w innym skrzydle); bez `czesci` w bryle cała połać.
      czesci: p.czesci ? p.czesci.map((c) => c.map(plan)) : [p.wierzcholki.map(plan)],
      czesciUv: p.czesci ? p.czesci.map((c) => c.map((q) => uvPolaci(p, q))) : [uvW],
      spadek: plaska ? null : kierunekSpadku(p.normalna),
      krawedzie: krawedzieWidoczne(p, rodzaje, []),
      // Na rzucie z góry znika też część pod okapem wyższego skrzydła (strefa `pod_dachem`).
      krawedzieZGory: krawedzieWidoczne(p, rodzaje, przeszkody
        .filter((o) => o.polacId === p.id && o.rodzaj === 'pod_dachem')
        .map((o) => o.obrys.map((q) => { const x = zUv(p, q); return [x[0], x[2]] }))),
      kosze: (bryla.kosze || []).filter((k) => koszNaPolaci(p, k)).map((k) => k.map((q) => uvPolaci(p, q))),
      obszarRoboczy: plaska ? [] : obszarRoboczy(p, { okap: margines, kalenica: margines, boki: margines }),
      przeszkody: przeszkody.filter((o) => o.polacId === p.id).map((o) => {
        // Lukarna: krawędzie daszka z bryły (kalenica i okapy daszka), żeby na rzucie było widać, co to jest.
        const el = o.rodzaj === 'lukarna' ? (projekt.elementy || []).find((e) => e.id === (o.elementId ?? o.id)) : null
        const lukarna = el ? brylaLukarny(bryla, el) : null
        const linie = lukarna ? lukarna.dachy.flatMap((d) => d.map((q, j) => [q, d[(j + 1) % d.length]])) : []
        return {
          id: o.id, rodzaj: o.rodzaj, nazwa: o.nazwa || null,
          uv: o.obrys, plan: o.obrys.map((q) => plan(zUv(p, q))),
          strefaUv: o.strefa || null, strefaPlan: o.strefa ? o.strefa.map((q) => plan(zUv(p, q))) : null,
          liniePlan: linie.map((l) => l.map(plan)), linieUv: linie.map((l) => l.map((q) => uvPolaci(p, q))),
        }
      }),
      zrodlo: p,
      moduly: [],
    }
  })
  const poId = new Map(polacie.map((p) => [p.id, p]))
  const obszar = grunt && Array.isArray(projekt.grunt?.obszar) && projekt.grunt.obszar.length >= 3
    ? projekt.grunt.obszar.map(([x, z]) => naRzut([x, z])) : null

  // Moduły do połaci; grunt (i moduły bez znanej połaci) jako jedno pole bez połaci.
  const bezPolaci = []
  for (const m of uklad.moduly || []) {
    const naroza = [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([a, b]) => m.srodek.map((c, i) => c + m.u[i] * (a * m.szer) / 2 + m.v[i] * (b * m.wys) / 2))
    const p = poId.get(m.polacId)
    const wpis = { id: m.id, rzad: m.rzad, kol: m.kol, plan: naroza.map(plan) }
    if (p && !p.plaska) {
      const [uc, vc] = uvPolaci(p.zrodlo, m.srodek)
      wpis.uv = [uc - m.szer / 2, vc - m.wys / 2, uc + m.szer / 2, vc + m.wys / 2]
    }
    if (p) p.moduly.push(wpis)
    else bezPolaci.push(wpis)
  }
  if (bezPolaci.length || grunt) {
    polacie.push({
      id: 'grunt', grunt: true, plaska: true, nachylenie: null, azymut: null, kierunek: null,
      plan: obszar || [], uv: null, czesci: obszar ? [obszar] : [], czesciUv: [], spadek: null, krawedzie: [], krawedzieZGory: [], kosze: [],
      obszarRoboczy: [], przeszkody: [], moduly: bezPolaci,
    })
  }

  // Litery: najpierw połacie z modułami (więcej modułów, potem bliżej południa), dalej reszta w kolejności bryły.
  const odPoludnia = (p) => (p.azymut == null ? 180 : Math.abs(normujKat(p.azymut) - 180))
  const zModulami = polacie.filter((p) => p.moduly.length).sort((a, b) => (b.moduly.length - a.moduly.length) || (odPoludnia(a) - odPoludnia(b)))
  const kolejnosc = [...zModulami, ...polacie.filter((p) => !p.moduly.length && !p.grunt)]
  kolejnosc.forEach((p, i) => { p.litera = LITERY[i] || `P${i + 1}` })

  // Numeracja jedna (decyzja właściciela 2026-10-23): po połaciach w kolejności liter, w połaci od kalenicy w dół
  // i od lewej patrząc na połać; na dachu płaskim i gruncie z góry kartki w dół i od lewej.
  let nr = 0
  for (const p of zModulami) {
    const sr = (m) => [(m.plan[0][0] + m.plan[2][0]) / 2, (m.plan[0][1] + m.plan[2][1]) / 2]
    const klucz = (m) => (m.uv ? [-(m.uv[1] + m.uv[3]) / 2, (m.uv[0] + m.uv[2]) / 2] : [sr(m)[1], sr(m)[0]])
    p.moduly.sort((a, b) => {
      const [a1, a2] = klucz(a)
      const [b1, b2] = klucz(b)
      return Math.abs(a1 - b1) > 0.05 ? a1 - b1 : a2 - b2
    })
    for (const m of p.moduly) { nr += 1; m.nr = nr }
    // Rzędy na połaci skośnej: pas v i przekrój połaci na dole rzędu (do wymiarów w prawdziwych długościach).
    if (!p.plaska) {
      const rzedy = new Map()
      for (const m of p.moduly) {
        const r = rzedy.get(m.rzad) || { rzad: m.rzad, va: Infinity, vb: -Infinity, moduly: [] }
        r.va = Math.min(r.va, m.uv[1])
        r.vb = Math.max(r.vb, m.uv[3])
        r.moduly.push(m)
        rzedy.set(m.rzad, r)
      }
      // Przekrój przez widoczne kawałki: od pierwszego do ostatniego punktu połaci na tej wysokości.
      const przekroj = (v) => {
        const z = p.czesciUv.map((c) => przekrojPoziomy(c, v)).filter(Boolean)
        return z.length ? [Math.min(...z.map((x) => x[0])), Math.max(...z.map((x) => x[1]))] : null
      }
      p.rzedy = [...rzedy.values()].sort((a, b) => a.va - b.va).map((r) => ({ ...r, przekroj: przekroj(r.va + 1e-6) }))
    } else {
      p.rzedy = []
    }
  }
  for (const p of polacie) { if (!p.rzedy) p.rzedy = []; delete p.zrodlo }

  const sciany = grunt ? [] : (bryla.obrysy || []).map((o) => o.punkty.map(naRzut))
  const wszystkie = [
    ...polacie.flatMap((p) => [...p.plan, ...p.moduly.flatMap((m) => m.plan), ...p.przeszkody.flatMap((o) => o.plan)]),
    ...sciany.flat(),
  ]
  return {
    rodzaj: grunt ? 'grunt' : 'dach',
    modul: { nazwa: nazwaModulu(projekt.moduly, modul), moc: modul.moc, dlugosc: modul.dlugosc, szerokosc: modul.szerokosc },
    liczba: nr,
    mocKwp: Math.round(nr * modul.moc) / 1000,
    margines,
    obrot,
    polnoc,
    polacie: kolejnosc.concat(polacie.filter((p) => p.grunt && !p.moduly.length)),
    kosze: grunt ? [] : (bryla.kosze || []).map((k) => k.map(plan)),
    sciany,
    obszar,
    zakres: wszystkie.length ? zakresPunktow(wszystkie) : { minX: 0, maxX: 1, minY: 0, maxY: 1 },
  }
}
